/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * THE TIER IN FORCE (model tier pending, 29/10/2026).
 *
 * The tier a case runs at is the risk engine's (report.riskAssessment, with the
 * operating tier the re tier records when a hold or an adjustment binds) or the
 * MLRO's recorded adjustment (report.tierAdjustment.effectiveTier). It is never
 * the model's own proposal, report.riskTier as the generation wrote it.
 *
 * Until this change report.riskTier was read as the tier everywhere, and on
 * three paths it was the model's: a documents first build or Reprocess whose
 * engine failed with no earlier assessment (df-process kept the model's
 * board tier and wrote risk_tier from it), an officer regenerate whose server
 * re tier failed (the dashboard had already saved the model's risk_tier), and
 * an interview submit, where no engine ran until something else re tiered the
 * case. The notify email, the automatic source of wealth plan and the
 * proportionality pass read it in that window.
 *
 * tierInForce answers the engine tier, the MLRO's adjustment, or null. Null
 * means "Assessment pending": no tier is in force, sign off is refused, and the
 * officer is told to Reprocess. The model's view stays on the file as
 * report.aiRiskTier and riskAssessment.modelDisagreement, labelled as not
 * governing, for the officer only.
 *
 * Pure, no I/O. Read by the dashboard and by the api routes.
 */

export const ASSESSMENT_PENDING = 'Assessment pending';

export const SIGNOFF_PENDING_TEXT = 'Sign off is not available yet. The risk assessment has not completed, so there is no tier to sign off. Reprocess the case (Regenerate for an individual) to complete it.';

// Owner (30/10/2026): an officer save through update-session keeps the stored
// engine record and tier; only Adjust tier and a reassessment change them. The
// route answers which keys it kept (kept), and the dashboard says so plainly.
export const KEPT_ON_SAVE_TEXT = 'Saved. The risk assessment and tier were not changed by this save: they change only through Adjust tier or a reassessment.';
/**
 * The dashboard's case row with the stored values a save kept (the answer's
 * stored: report keys, null meaning absent, and risk_tier), so the tier chip,
 * Assessment pending and the Approve state show the stored record, never the
 * stale copy. The row unchanged when nothing was kept.
 */
export function mergeKeptIntoRow(row, body) {
  if (!row || typeof row !== 'object' || !body || !Array.isArray(body.kept) || !body.kept.length || !body.stored) return row;
  const out = { ...row };
  if (body.stored.report && typeof body.stored.report === 'object') {
    const rep = { ...((row.report && typeof row.report === 'object') ? row.report : {}) };
    for (const [k, v] of Object.entries(body.stored.report)) { if (v === null) delete rep[k]; else rep[k] = v; }
    out.report = rep;
  }
  if (Object.prototype.hasOwnProperty.call(body.stored, 'risk_tier')) out.risk_tier = body.stored.risk_tier;
  return out;
}

/** The notice for a save answer whose kept list is not empty, else null. */
export function keptOnSaveNotice(body) {
  return body && Array.isArray(body.kept) && body.kept.length ? KEPT_ON_SAVE_TEXT : null;
}
export const DECLINE_PENDING_TEXT = 'You can still decline the case. The decision record and the case notes will say it was declined before an engine risk assessment existed.';

export const DECLINED_BEFORE_ASSESSMENT_NOTE = 'This case was declined before an engine risk assessment existed. No risk tier was in force when the decision was recorded, so the decline does not rest on an assessed tier.';

export const SECOND_SIGNOFF_PENDING_TEXT = 'This file cannot be approved yet. The risk assessment has not completed, so there is no tier to approve. The compliance team will send you a new link when it is ready.';

const RANK = { Low: 1, Medium: 2, High: 3 };
const valid = (t) => (t === 'Low' || t === 'Medium' || t === 'High') ? t : null;
const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

/**
 * A genuine engine assessment (review, 30/10/2026: every engine version).
 * What the risk engine wrote, version by version (git history of
 * api/_risk-engine.js):
 *   from the first upload (30/06/2026) to v128: engineVersion, tier,
 *     computedAt and computedBy "CompliantID Risk Engine v<version>";
 *   v375 and v378 (03/08/2026): also methodologyVersion and categories;
 *   v381 (03/08/2026) onward: also rulesEvaluated.
 * Every version wrote engineVersion and that computedBy, so a record carrying
 * both (with a tier of Low, Medium or High) is the engine's; so is a record
 * with the full current shape (methodologyVersion, a rulesEvaluated object,
 * categories). A bare { tier }, or the hollow record a model pass once
 * returned (rulesEvaluated, methodologyVersion and engineVersion dropped,
 * STGG 13/08/2026), is no assessment and puts no tier in force. Client writes
 * of these keys are refused besides (api/update-session.js).
 */
export function isEngineAssessment(a) {
  if (!a || typeof a !== 'object' || Array.isArray(a) || !valid(a.tier)) return false;
  const stamped = typeof a.computedBy === 'string' && a.computedBy.startsWith('CompliantID Risk Engine') && a.engineVersion != null && String(a.engineVersion).trim() !== '';
  const fullShape = !!(a.methodologyVersion && a.rulesEvaluated && typeof a.rulesEvaluated === 'object' && a.categories);
  return stamped || fullShape;
}

/** The tier in force on a report, or null. Never reads report.riskTier. */
export function tierFromReport(report) {
  const r = (report && typeof report === 'object' && !Array.isArray(report)) ? report : null;
  if (!r) return null;
  const ra = isEngineAssessment(r.riskAssessment) ? r.riskAssessment : null;
  const adj = (r.tierAdjustment && typeof r.tierAdjustment === 'object' && valid(r.tierAdjustment.effectiveTier)) ? r.tierAdjustment : null;
  if (!ra && !adj) return null;
  const engineOperating = ra ? (valid(ra.operatingTier) || ra.tier) : null; // operatingTier is trusted only inside a genuine assessment
  if (!adj) return engineOperating;
  if (!ra) return adj.effectiveTier;
  // An assessment computed after the adjustment may raise the case above it
  // (the re tier's raise only rule, _retier-core resolveEffectiveTier); it
  // never lowers it. An adjustment made after the assessment governs.
  const raAt = Date.parse(ra.computedAt || '');
  const adjAt = Date.parse(adj.at || '');
  if (Number.isFinite(raAt) && Number.isFinite(adjAt) && raAt > adjAt && (RANK[engineOperating] || 0) > (RANK[adj.effectiveTier] || 0)) return engineOperating;
  return adj.effectiveTier;
}

/** The tier in force on a report (an explicit name for tierFromReport). */
export const tierInForceFromReport = tierFromReport;

/**
 * The tier in force on a session row.
 *   The row was read with its report: the report decides, and a row whose
 *   report is null or not an object has no tier in force, whatever its
 *   risk_tier column says (the column could hold a model's tier from before
 *   this change).
 *   The row was read without its report (a list read of columns only, the
 *   dashboard's live status poll): the risk_tier column, which every writer
 *   now sets from the tier in force of the report it writes.
 */
export function tierInForceFromRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  if (hasOwn(row, 'report')) return tierFromReport(row.report);
  return valid(row.risk_tier);
}

/**
 * NO GUESSING THE SHAPE (review, 30/10/2026). tierInForce read any object
 * carrying a 'report' or a 'risk_tier' key as a session row, so a REPORT
 * holding a top level "risk_tier": "Low" (a model's reply, a staff write) or a
 * "report" key returned that forged value as the tier in force. Each function
 * now reads one shape only: the plain names take a REPORT; the FromRow names
 * take a session ROW (its report decides when it was read with it, else its
 * risk_tier column).
 */
/**
 * Keys a report never carries (review, 30/10/2026): a top level 'risk_tier' or
 * 'report' inside a report is a session row's shape, never a report's. Every
 * report write drops them (a model reply, a dashboard copy, a staff write), and
 * the draft staff guard refuses a staff write of them.
 */
export const REPORT_FORBIDDEN_KEYS = Object.freeze(['risk_tier', 'report']);
/** Drops the forbidden keys from a report (mutates); returns the keys it dropped. */
export function dropForbiddenReportKeys(report) {
  const dropped = [];
  if (!report || typeof report !== 'object' || Array.isArray(report)) return dropped;
  for (const k of REPORT_FORBIDDEN_KEYS) if (Object.prototype.hasOwnProperty.call(report, k)) { delete report[k]; dropped.push(k); }
  return dropped;
}

/** The tier in force on a REPORT. */
export function tierInForce(report) { return tierFromReport(report); }

/** True when no tier is in force on a REPORT. */
export function assessmentPending(report) { return tierFromReport(report) === null; }
/** True when no tier is in force on a session ROW. */
export function assessmentPendingFromRow(row) { return tierInForceFromRow(row) === null; }

/** The tier in force on a REPORT, or the words "Assessment pending". */
export function tierOrPending(report) { return tierFromReport(report) || ASSESSMENT_PENDING; }
/** The tier in force on a session ROW, or the words "Assessment pending". */
export function tierOrPendingFromRow(row) { return tierInForceFromRow(row) || ASSESSMENT_PENDING; }

/** Why the assessment is pending on a REPORT, in the words the platform recorded, or null. */
export function assessmentPendingReason(report) {
  const r = report;
  if (!r || typeof r !== 'object' || Array.isArray(r)) return null;
  if (r.assessmentPending && typeof r.assessmentPending === 'object' && r.assessmentPending.reason) return String(r.assessmentPending.reason);
  if (r.riskEngineError && typeof r.riskEngineError === 'object' && r.riskEngineError.message && !r.riskEngineError.retainedPrior) return 'the risk engine could not run (' + String(r.riskEngineError.message) + ')';
  return null;
}
/** Why the assessment is pending on a session ROW (from its report), or null. */
export function assessmentPendingReasonFromRow(row) {
  return (row && typeof row === 'object' && row.report && typeof row.report === 'object') ? assessmentPendingReason(row.report) : null;
}

/** The record written on a report when no tier can be put in force. */
export function pendingRecord(reason, extra = {}) {
  return { reason: String(reason || 'the risk assessment has not run yet'), at: new Date().toISOString(), ...extra };
}

/**
 * What the officer is told when the server re tier after a regenerate failed:
 * the platform's own error, word for word, and what that leaves on the case.
 * Null when the re tier ran. Read by both regenerate paths in the dashboard.
 */
export function retierFailureMessage(resp) {
  // Fix B (owner, 30/10/2026): a re tier that finished after a newer assessment was stored is not written; said plainly.
  if (resp && resp.retierSuperseded) return 'The report was regenerated. Its risk assessment was not saved because a newer one is already on the case' + (resp.newerAssessment && resp.newerAssessment.tier ? ' (' + resp.newerAssessment.tier + ')' : '') + ', and the newer one stands.';
  if (!resp || !resp.retierError) return null;
  return 'The report was regenerated, but the risk assessment did not complete: ' + String(resp.retierError) +
    (resp.assessment === 'earlier assessment stands'
      ? ' The earlier assessment stands until it runs again.'
      : ' No risk tier is in force, so the case shows Assessment pending and sign off is not available.') +
    ' Reprocess to complete it.';
}

/**
 * The officer's hover text on an "Assessment pending" chip: why, and the
 * model's suggestion labelled as not governing. Never shown to the client.
 */
export function pendingTitle(row) {
  // A session ROW (the dashboard's selected case): the report decides.
  const r = (row && typeof row === 'object' && row.report && typeof row.report === 'object') ? row.report : {};
  const why = assessmentPendingReason(r);
  const model = valid(r && r.aiRiskTier);
  return 'No risk tier is in force: the risk assessment has not completed' + (why ? ' (' + why + ')' : '') + '.' +
    (model ? ' The document analysis suggested ' + model + '; that suggestion is not governing.' : '') +
    ' Reprocess to complete it.';
}
